import {ChartData} from 'chart.js';
import * as I from 'immutable';
import React from 'react';

import {ApiOrganization, getAreaUnit} from 'app/modules/Remote/Organization';
import * as C from 'app/utils/constants';
import {convert} from 'app/utils/conversionUtils';

import {PieGroup} from '../GenericChart/PieGroupControls';
import {FeatureMetadataApiResponse} from '../types';
import {BaseLensStatsWidget} from './BaseLensStatsWidget';
import {DrilldownTableRow} from './LensStatsDrilldownTable';
import {EMPTY_PIE_CHART_DATA, generateChartColors} from '../utils/chartUtils';

/**
 * Widget that displays a pie chart of property areas by portfolio
 */
const VALID_AREA_GROUPINGS: PieGroup[] = [
  {
    label: 'Portfolio',
    id: 'portfolio',
    singleMembership: true,
    groupingField: 'projectId',
    labelField: 'projectName',
  },
  {
    label: 'Property Tag',
    id: 'propertyTag',
    singleMembership: false,
    groupingField: 'tags',
    labelField: 'tags',
  },
];

export const OrgAreaWidget: React.FC<{
  organization: I.ImmutableOf<ApiOrganization>;
  featureMetadata: FeatureMetadataApiResponse[] | undefined;
  isFetching: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  error?: Error | null;
  percentLoaded: number | undefined;
}> = ({
  organization,
  featureMetadata,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  error,
  percentLoaded,
}) => {
  const [selectedPieGroup, setSelectedPieGroup] = React.useState<PieGroup>(VALID_AREA_GROUPINGS[0]);

  const anyPropertyHasTags = featureMetadata?.some((featureMetadatum) =>
    Object.values(featureMetadatum).some((feature) => feature.tags?.length > 0)
  );

  const orgAreaUnit = getAreaUnit(organization);
  const organizationId = organization.get('id');
  const areaUnitLabel = orgAreaUnit === C.UNIT_AREA_ACRE ? 'acres' : 'hectares';

  // create chartjs pie datasets: a single pie chart is an array of datasets (usually 1) and an array of labels
  // each dataset has a data key with an array of values, one per label. plus each dataset has
  // metadata like background data, etc.
  // eg, {labels: ['Portfolio A', 'Portfolio B'], datasets: [{data: [1, 2], backgroundColor: 'red'}]}

  const chartData: ChartData<'pie'> = React.useMemo(() => {
    if (!featureMetadata) {
      return EMPTY_PIE_CHART_DATA;
    }

    const areaSums: Record<string, number> = {};
    const labelMap: Record<string, string> = {};

    featureMetadata.forEach((featureDict) =>
      Object.values(featureDict).forEach((feature) => {
        const id = selectedPieGroup.id === 'portfolio' ? feature.projectId! : feature.tags?.[0];
        const area = convert(feature.areaAcres, 'areaAcre', orgAreaUnit) || 0;
        if (!id) return;

        areaSums[id] = (areaSums[id] || 0) + area;
        labelMap[id] = selectedPieGroup.id === 'portfolio' ? feature.projectName : id;
      })
    );

    const labels = Object.keys(areaSums).map((id) => labelMap[id]);
    const data = Object.values(areaSums);
    const colors = generateChartColors(data.length);

    if (data.length === 0) {
      return EMPTY_PIE_CHART_DATA;
    }

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
        },
      ],
    };
  }, [featureMetadata, orgAreaUnit, selectedPieGroup.id]);

  const tableData = React.useMemo<DrilldownTableRow[]>(() => {
    if (!featureMetadata) return [];

    const rows: DrilldownTableRow[] = [];

    featureMetadata.forEach((featureDict) => {
      Object.entries(featureDict).forEach(([featureId, feature]) => {
        rows.push({
          featureId: featureId,
          projectId: feature.projectId,
          projectName: feature.projectName || 'Unknown Portfolio',
          featureName: feature.name || 'Unknown Property',
          propertyTags: feature.tags || [],
          organizationId: organizationId,
        });
      });
    });

    const sortedRows = rows.sort((a, b) => a.projectName.localeCompare(b.projectName));

    return sortedRows;
  }, [featureMetadata, organizationId]);

  // Continuously fetch data page by page so we can watch our data stream in
  // This is important for orgs with hundreds of features
  React.useEffect(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  return (
    <BaseLensStatsWidget
      error={error}
      widgetConfig={{
        title: `Property Areas by ${selectedPieGroup.label}`,
        type: 'chart',
        id: -3,
        isPublished: true,
      }}
      loading={!!(isFetching || isFetchingNextPage)}
      showControls={!!anyPropertyHasTags}
      pieGroups={VALID_AREA_GROUPINGS}
      selectedPieGroup={selectedPieGroup}
      setSelectedPieGroup={setSelectedPieGroup}
      chartData={chartData}
      plugins={[]}
      countItemLabel={areaUnitLabel}
      showDateRangeInput={false}
      percentLoaded={percentLoaded}
      tableData={tableData}
      organization={organization}
    />
  );
};
